import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseHttpService } from './base-http.service';
import { 
  SalesStats,
  UpdatePlanRequest,
  UpdateActualRequest,
  UpdateNotesRequest,
  SalesStatsUsersResponse,
  getCurrentWeekInfo
} from '../interfaces/sales-stats.interface';

@Injectable({
  providedIn: 'root'
})
export class SalesStatsService extends BaseHttpService {

  private baseUrl = '/sales-stats';

  constructor(
    http: HttpClient,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    super(http, platformId);
  }

  /**
   * Adott felhasználó adott heti sales statisztikáinak lekérdezése
   */
  getUserWeekStats(userId: number, weekNumber: number, year: number): Observable<SalesStats> {
    return this.get<SalesStats>(`${this.baseUrl}/user/${userId}/week/${weekNumber}/year/${year}`);
  }

  /**
   * Adott felhasználó aktuális heti sales statisztikáinak lekérdezése
   */
  getUserCurrentWeekStats(userId: number): Observable<SalesStats> {
    return this.get<SalesStats>(`${this.baseUrl}/user/${userId}/current-week`);
  }

  /**
   * Sales statisztikák tervezett értékeinek frissítése
   */
  updatePlanValues(request: UpdatePlanRequest): Observable<SalesStats> {
    return this.put<SalesStats>(`${this.baseUrl}/plan/update`, request);
  }

  /**
   * Sales statisztikák tényleges értékének rögzítése
   */
  logActualValue(request: UpdateActualRequest): Observable<SalesStats> {
    return this.put<SalesStats>(`${this.baseUrl}/actual/log`, request);
  }

  /**
   * Sales statisztikák jegyzetek frissítése
   */
  updateNotes(request: UpdateNotesRequest): Observable<SalesStats> {
    return this.put<SalesStats>(`${this.baseUrl}/notes/update`, request);
  }

  /**
   * Felhasználó egy hónap összes heti sales statisztikáinak lekérdezése
   * @param userId - Felhasználó ID
   * @param month - Hónap (1-12) 
   * @param year - Év
   */
  getMonthStats(userId: number, month: number, year: number): Observable<SalesStats[]> {
    // Meghatározzuk a hónap heteit
    const weekNumbers = this.getWeekNumbersForMonth(month, year);
    
    // Párhuzamosan lekérjük az összes hét adatait
    const weekRequests = weekNumbers.map(weekNumber => 
      this.getUserWeekStats(userId, weekNumber, year)
    );

    return forkJoin(weekRequests);
  }

  /**
   * Több felhasználó, több hét KÜM statisztikáinak egyszerre történő lekérdezése (bulk)
   * Egyetlen HTTP kérés a szerver felé, egyetlen SQL query az adatbázisban
   */
  getBulkStats(userIds: number[], weekNumbers: number[], year: number): Observable<SalesStats[]> {
    return this.post<SalesStats[]>(`${this.baseUrl}/bulk`, {
      user_ids: userIds,
      week_numbers: weekNumbers,
      year
    });
  }

  /**
   * Havi KÜM statisztikák lekérdezése több felhasználóhoz egyetlen kéréssel
   * @returns Objektum userId kulccsal és a felhasználó heti statisztikái tömbjével
   */
  getMonthStatsBulk(userIds: number[], month: number, year: number): Observable<{ [userId: number]: SalesStats[] }> {
    const weekNumbers = this.getWeekNumbersForMonth(month, year);
    return this.getBulkStats(userIds, weekNumbers, year).pipe(
      map(allStats => {
        const grouped: { [userId: number]: SalesStats[] } = {};
        userIds.forEach(id => grouped[id] = []);
        allStats.forEach(stat => {
          if (grouped[stat.user_id]) {
            grouped[stat.user_id].push(stat);
          }
        });
        return grouped;
      })
    );
  }

  /**
   * Meghatározza egy adott hónap heteinek számait
   */
  getWeekNumbersForMonth(month: number, year: number): number[] {
    const weekNumbers: Set<number> = new Set();
    
    // Hónap első napja
    const firstDay = new Date(year, month - 1, 1);
    // Hónap utolsó napja
    const lastDay = new Date(year, month, 0);
    
    // Végigmegyünk a hónap összes napján
    for (let date = new Date(firstDay); date <= lastDay; date.setDate(date.getDate() + 1)) {
      const weekInfo = this.getWeekNumberFromDate(date);
      weekNumbers.add(weekInfo.weekNumber);
    }
    
    return Array.from(weekNumbers).sort((a, b) => a - b);
  }

  /**
   * Hét szám kinyerése dátumból
   */
  private getWeekNumberFromDate(date: Date): { weekNumber: number; year: number } {
    const tempDate = new Date(date.getTime());
    tempDate.setHours(0, 0, 0, 0);
    tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
    const week1 = new Date(tempDate.getFullYear(), 0, 4);
    const weekNumber = 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
    
    return {
      weekNumber,
      year: tempDate.getFullYear()
    };
  }

  /**
   * Adott hónapban sales statisztikákkal rendelkező felhasználók lekérdezése
   */
  getUsersWithData(month: number, year: number): Observable<SalesStatsUsersResponse> {
    const params = new HttpParams()
      .set('month', month.toString())
      .set('year', year.toString());
    
    return this.get<SalesStatsUsersResponse>(`${this.baseUrl}/users-with-data`, params);
  }

  /**
   * Üres sales stats objektum létrehozása
   */
  createEmptyStats(userId: number, weekNumber?: number, year?: number): SalesStats {
    const weekInfo = weekNumber && year ? { weekNumber, year } : getCurrentWeekInfo();
    
    return {
      id: 0,
      user_id: userId,
      week_number: weekInfo.weekNumber,
      year: weekInfo.year,
      appointment_scheduled_plan: [0, 0, 0, 0, 0],
      appointment_scheduled_actual: [0, 0, 0, 0, 0],
      succesfulfirstmeetings_plan: [0, 0, 0, 0, 0],
      succesfulfirstmeetings_actual: [0, 0, 0, 0, 0],
      successfulassessments_plan: [0, 0, 0, 0, 0],
      successfulassessments_actual: [0, 0, 0, 0, 0],
      successfulproposalpresentations_plan: [0, 0, 0, 0, 0],
      successfulproposalpresentations_actual: [0, 0, 0, 0, 0],
      successfulclosures_plan: [0, 0, 0, 0, 0],
      successfulclosures_actual: [0, 0, 0, 0, 0],
      notes: ['', '', '', '', '']
    };
  }

  /**
   * Hét napjainak nevei magyarul
   */
  getDayNames(): string[] {
    return ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek'];
  }

  /**
   * Hét napjainak indexei (0-4)
   */
  getDayIndices(): number[] {
    return [0, 1, 2, 3, 4];
  }

  /**
   * Sales statisztikák validálása
   */
  validateSalesStats(stats: Partial<SalesStats>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Kötelező mezők ellenőrzése
    if (!stats.user_id) {
      errors.push('Felhasználó azonosító kötelező');
    }

    if (!stats.week_number || stats.week_number < 1 || stats.week_number > 53) {
      errors.push('Hét száma 1 és 53 között kell legyen');
    }

    if (!stats.year || stats.year < 2020) {
      errors.push('Érvényes év kötelező');
    }

    // Array mezők ellenőrzése
    const arrayFields = [
      'appointment_scheduled_plan', 'appointment_scheduled_actual',
      'succesfulfirstmeetings_plan', 'succesfulfirstmeetings_actual',
      'successfulassessments_plan', 'successfulassessments_actual',
      'successfulproposalpresentations_plan', 'successfulproposalpresentations_actual',
      'successfulclosures_plan', 'successfulclosures_actual'
    ];

    arrayFields.forEach(field => {
      const value = (stats as any)[field];
      if (value && (!Array.isArray(value) || value.length !== 5)) {
        errors.push(`${field} 5 elemű tömb kell legyen`);
      }
    });

    // Negatív értékek ellenőrzése
    arrayFields.forEach(field => {
      const value = (stats as any)[field];
      if (value && Array.isArray(value)) {
        value.forEach((val: number, index: number) => {
          if (typeof val === 'number' && val < 0) {
            errors.push(`${field}[${index}] nem lehet negatív`);
          }
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Sales statisztikák összesítése
   */
  calculateWeeklyTotals(stats: SalesStats): { [key: string]: { plan: number; actual: number } } {
    const totals: { [key: string]: { plan: number; actual: number } } = {};

    const metrics = [
      'appointmentscheduled',
      'succesfulfirstmeetings',
      'successfulassessments',
      'successfulproposalpresentations',
      'successfulclosures'
    ];

    metrics.forEach(metric => {
      const planField = `${metric}_plan` as keyof SalesStats;
      const actualField = `${metric}_actual` as keyof SalesStats;
      
      const planArray = stats[planField] as number[] || [0, 0, 0, 0, 0];
      const actualArray = stats[actualField] as number[] || [0, 0, 0, 0, 0];
      
      totals[metric] = {
        plan: planArray.reduce((sum, val) => sum + (val || 0), 0),
        actual: actualArray.reduce((sum, val) => sum + (val || 0), 0)
      };
    });

    return totals;
  }

  /**
   * Teljesítmény százalék számítása
   */
  calculatePerformancePercentage(plan: number, actual: number): number {
    if (plan === 0) return actual > 0 ? 100 : 0;
    return Math.round((actual / plan) * 100);
  }

  /**
   * Havi összesítő statisztikák számítása
   */
  calculateMonthlyTotals(weeklyStats: SalesStats[]): { [key: string]: { plan: number; actual: number; performance: number } } {
    const monthlyTotals: { [key: string]: { plan: number; actual: number; performance: number } } = {};

    const metrics = [
      'appointmentscheduled',
      'succesfulfirstmeetings', 
      'successfulassessments',
      'successfulproposalpresentations',
      'successfulclosures'
    ];

    metrics.forEach(metric => {
      let totalPlan = 0;
      let totalActual = 0;

      weeklyStats.forEach(weekStats => {
        const weeklyTotals = this.calculateWeeklyTotals(weekStats);
        totalPlan += weeklyTotals[metric]?.plan || 0;
        totalActual += weeklyTotals[metric]?.actual || 0;
      });

      monthlyTotals[metric] = {
        plan: totalPlan,
        actual: totalActual,
        performance: this.calculatePerformancePercentage(totalPlan, totalActual)
      };
    });

    return monthlyTotals;
  }
}